import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Images, Play, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { workCategories, type BeforeAfterVideo, type BeforeAfterWork, type WorkCategory, type WorkItem } from "@/lib/work-gallery";

type GalleryFilter = "all" | WorkCategory;

export function WorkGallery({ items, showFilters = false }: { items: WorkItem[]; showFilters?: boolean }) {
  const [filter, setFilter] = useState<GalleryFilter>("all");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const visibleItems = useMemo(
    () => (filter === "all" ? items : items.filter((item) => item.category === filter)),
    [filter, items],
  );

  useEffect(() => setSelectedIndex(null), [filter]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedIndex(null);
      if (event.key === "ArrowLeft") setSelectedIndex((current) => current === null ? null : (current - 1 + visibleItems.length) % visibleItems.length);
      if (event.key === "ArrowRight") setSelectedIndex((current) => current === null ? null : (current + 1) % visibleItems.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedIndex, visibleItems.length]);

  const selected = selectedIndex === null ? undefined : visibleItems[selectedIndex];

  return (
    <>
      {showFilters && (
        <div className="mb-10 flex flex-wrap gap-2" role="group" aria-label="Filter photos by service">
          <Button type="button" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>All work</Button>
          {workCategories.map((category) => (
            <Button key={category.id} type="button" variant={filter === category.id ? "default" : "outline"} onClick={() => setFilter(category.id)}>
              {category.label}
            </Button>
          ))}
        </div>
      )}

      {visibleItems.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleItems.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className="group relative block aspect-[4/3] overflow-hidden rounded-lg border border-border bg-card text-left shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setSelectedIndex(index)}
              aria-label={`Open ${item.alt}`}
            >
              {item.type === "before-after" || item.type === "before-after-video" ? (
                <BeforeAfter item={item} compact />
              ) : item.type === "video" ? (
                <video src={item.src} muted playsInline preload="metadata" className="h-full w-full object-cover" />
              ) : (
                <img src={item.src} alt={item.alt} loading="lazy" width={800} height={600} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
              )}
              <span className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-background/90 text-foreground shadow-soft" aria-hidden="true">
                {item.type === "video" || item.type === "before-after-video" ? <Play className="h-4 w-4" /> : <Images className="h-4 w-4" />}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="py-12 text-center text-muted-foreground">More photos from this service are coming soon.</p>
      )}

      {selected && selectedIndex !== null && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-foreground/90 p-4 md:p-10" role="dialog" aria-modal="true" aria-label="Cleaning work photo viewer" onMouseDown={(event) => event.target === event.currentTarget && setSelectedIndex(null)}>
          <Button type="button" variant="secondary" size="icon" className="absolute right-4 top-4 z-10 rounded-full" onClick={() => setSelectedIndex(null)} aria-label="Close photo viewer"><X /></Button>
          {visibleItems.length > 1 && (
            <>
              <Button type="button" variant="secondary" size="icon" className="absolute left-4 top-1/2 z-10 rounded-full" onClick={() => setSelectedIndex((selectedIndex - 1 + visibleItems.length) % visibleItems.length)} aria-label="Previous photo"><ChevronLeft /></Button>
              <Button type="button" variant="secondary" size="icon" className="absolute right-4 top-1/2 z-10 rounded-full" onClick={() => setSelectedIndex((selectedIndex + 1) % visibleItems.length)} aria-label="Next photo"><ChevronRight /></Button>
            </>
          )}
          <div className="h-auto max-h-[85vh] w-full max-w-6xl overflow-hidden rounded-lg bg-background shadow-lift">
            {selected.type === "before-after" || selected.type === "before-after-video" ? (
              <BeforeAfter item={selected} />
            ) : selected.type === "video" ? (
              <video src={selected.src} controls autoPlay playsInline className="max-h-[85vh] w-full object-contain" />
            ) : (
              <img src={selected.src} alt={selected.alt} className="max-h-[85vh] w-full object-contain" />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function BeforeAfter({ item, compact = false }: { item: BeforeAfterWork | BeforeAfterVideo; compact?: boolean }) {
  const [position, setPosition] = useState(50);

  if (item.type === "before-after-video") {
    return (
      <div className={cn("grid w-full gap-1 bg-muted", compact ? "h-full grid-cols-2" : "grid-cols-1 sm:grid-cols-2")}>
        {(["before", "after"] as const).map((side) => (
          <div key={side} className="relative h-full w-full overflow-hidden">
            <video
              src={item[side]}
              controls={!compact}
              muted
              playsInline
              preload="metadata"
              className={cn("h-full w-full object-cover", compact ? "" : "max-h-[75vh]")}
            />
            <span className="absolute left-2 top-2 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold capitalize text-foreground">{side}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full overflow-hidden bg-muted", compact ? "h-full" : "aspect-[4/3]")}>
      <img src={item.after} alt={`After: ${item.alt}`} className="absolute inset-0 h-full w-full object-cover" />
      <img
        src={item.before}
        alt={`Before: ${item.alt}`}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      />
      <span className="absolute left-3 top-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground">Before</span>
      <span className="absolute right-3 top-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground">After</span>
      {!compact && (
        <input
          type="range"
          min="0"
          max="100"
          value={position}
          onChange={(event) => setPosition(Number(event.target.value))}
          className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
          aria-label="Move to compare before and after"
        />
      )}
      <span className="pointer-events-none absolute inset-y-0 w-0.5 bg-background shadow" style={{ left: `${position}%` }} aria-hidden="true" />
    </div>
  );
}